import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Slide from '@mui/material/Slide';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import * as React from 'react';

// -- type definitions --

export interface CarouselImage {
  src: string;
  alt: string;
}

export interface ImageCarouselProps {
  images: CarouselImage[];
  open: boolean;
  onClose: () => void;
  initialIndex?: number;
}

function getNavButtonSx(theme: Theme) {
  return {
    color: theme.palette.common.white,
    bgcolor: alpha(theme.palette.common.black, 0.45),
    '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.7) },
    '&.Mui-disabled': {
      color: alpha(theme.palette.common.white, 0.4),
      bgcolor: alpha(theme.palette.common.black, 0.2),
    },
  };
}

// -- component --

export default function ImageCarousel({
  images,
  open,
  onClose,
  initialIndex = 0,
}: ImageCarouselProps) {
  const theme = useTheme();
  const navButtonSx = getNavButtonSx(theme);
  const [index, setIndex] = React.useState(initialIndex);
  const [slideDirection, setSlideDirection] = React.useState<'left' | 'right'>(
    'left'
  );

  React.useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      setSlideDirection('left');
    }
  }, [open, initialIndex]);

  const atStart = index <= 0;
  const atEnd = index >= images.length - 1;

  const handlePrevious = () => {
    if (atStart) return;
    setSlideDirection('right');
    setIndex((current) => current - 1);
  };

  const handleNext = () => {
    if (atEnd) return;
    setSlideDirection('left');
    setIndex((current) => current + 1);
  };

  const activeImage = images[index];

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { bgcolor: alpha(theme.palette.common.black, 0.75), boxShadow: 24 },
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Exit button */}
        <IconButton
          onClick={onClose}
          aria-label="Close carousel"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
            ...navButtonSx,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Previous */}
        <IconButton
          onClick={handlePrevious}
          disabled={atStart}
          aria-label="Previous image"
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            ...navButtonSx,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        {/* Next */}
        <IconButton
          onClick={handleNext}
          disabled={atEnd}
          aria-label="Next image"
          sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            ...navButtonSx,
          }}
        >
          <ChevronRightIcon />
        </IconButton>

        {/* Image viewport with transition */}
        <Box
          sx={{
            width: '82%',
            maxWidth: 900,
            height: '72vh',
            maxHeight: 620,
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: 24,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Image index as key: triggers the animation on change */}
          <Slide direction={slideDirection} in mountOnEnter appear key={index}>
            <Box
              component="img"
              src={activeImage.src}
              alt={activeImage.alt}
              draggable={false}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Slide>
        </Box>
      </Box>
    </Modal>
  );
}
